import { supabase, TABLES } from './supabase';
import { PlayerStatistics } from '../types/player';

export class StatisticsService {
  // 获取所有玩家的统计信息
  static async getAllPlayerStatistics(): Promise<PlayerStatistics[]> {
    const { data, error } = await supabase
      .from(TABLES.PLAYER_STATISTICS)
      .select('*')
      .order('number');

    if (error) {
      throw new Error(`获取玩家统计失败: ${error.message}`);
    }

    return data.map(stat => ({
      playerId: stat.id,
      totalMatches: stat.total_matches || 0,
      wins: stat.wins || 0,
      draws: stat.draws || 0,
      losses: stat.losses || 0,
      restCount: 0, // 这个需要单独计算
    }));
  }

  // 获取特定玩家的统计信息
  static async getPlayerStatistics(playerId: string): Promise<PlayerStatistics | null> {
    const { data, error } = await supabase
      .from(TABLES.PLAYER_STATISTICS)
      .select('*')
      .eq('id', playerId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw new Error(`获取玩家统计失败: ${error.message}`);
    }

    return {
      playerId: data.id,
      totalMatches: data.total_matches || 0,
      wins: data.wins || 0,
      draws: data.draws || 0,
      losses: data.losses || 0,
      restCount: 0, // 这个需要单独计算
    };
  }

  // 获取配对统计信息
  static async getPairingStatistics(): Promise<any[]> {
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .select(`
        team1_player1_id,
        team1_player2_id,
        team2_player1_id,
        team2_player2_id,
        result,
        team1_player1:team1_player1_id(name, number),
        team1_player2:team1_player2_id(name, number),
        team2_player1:team2_player1_id(name, number),
        team2_player2:team2_player2_id(name, number)
      `);

    if (error) {
      throw new Error(`获取配对统计失败: ${error.message}`);
    }

    // 统计每个配对组合的胜负情况
    const pairingStats = new Map();

    data.forEach((match: any) => {
      // Team 1 配对
      const team1Key = [match.team1_player1_id, match.team1_player2_id].sort().join('-');
      const team1Player1 = match.team1_player1;
      const team1Player2 = match.team1_player2;
      const team1Names = `${team1Player1?.name || '未知'} & ${team1Player2?.name || '未知'}`;
      
      if (!pairingStats.has(team1Key)) {
        pairingStats.set(team1Key, {
          players: team1Names,
          wins: 0,
          draws: 0,
          losses: 0,
          total: 0,
        });
      }
      
      const team1Stat = pairingStats.get(team1Key)!;
      team1Stat.total++;
      if (match.result === 'win') team1Stat.wins++;
      else if (match.result === 'draw') team1Stat.draws++;
      else if (match.result === 'loss') team1Stat.losses++;

      // Team 2 配对
      const team2Key = [match.team2_player1_id, match.team2_player2_id].sort().join('-');
      const team2Player1 = match.team2_player1;
      const team2Player2 = match.team2_player2;
      const team2Names = `${team2Player1?.name || '未知'} & ${team2Player2?.name || '未知'}`;
      
      if (!pairingStats.has(team2Key)) {
        pairingStats.set(team2Key, {
          players: team2Names,
          wins: 0,
          draws: 0,
          losses: 0,
          total: 0,
        });
      }
      
      const team2Stat = pairingStats.get(team2Key)!;
      team2Stat.total++;
      // Team 2 的结果与 Team 1 相反
      if (match.result === 'win') team2Stat.losses++;
      else if (match.result === 'draw') team2Stat.draws++;
      else if (match.result === 'loss') team2Stat.wins++;
    });

    return Array.from(pairingStats.values()).sort((a, b) => b.total - a.total);
  }

  // 计算轮换公平性统计
  static async getRotationStatistics(): Promise<any> {
    // 获取所有比赛的队列快照
    const { data, error } = await supabase
      .from(TABLES.MATCHES)
      .select('queue_snapshot, played_at')
      .order('played_at');

    if (error) {
      throw new Error(`获取轮换统计失败: ${error.message}`);
    }

    const restCounts = new Map();
    
    data.forEach(match => {
      const queueSnapshot = match.queue_snapshot as any[];
      if (queueSnapshot && queueSnapshot.length > 4) {
        // 第5个人是休息的人
        const restingPlayer = queueSnapshot[4];
        const count = restCounts.get(restingPlayer.id) || 0;
        restCounts.set(restingPlayer.id, count + 1);
      }
    });

    // 获取玩家信息
    const { data: players } = await supabase
      .from(TABLES.PLAYERS)
      .select('id, name, number')
      .eq('is_active', true);

    const rotationStats = players?.map(player => ({
      playerId: player.id,
      playerName: player.name,
      playerNumber: player.number,
      restCount: restCounts.get(player.id) || 0,
    })) || [];

    return {
      totalMatches: data.length,
      rotationStats: rotationStats.sort((a, b) => a.playerNumber - b.playerNumber),
    };
  }
}